import { Vector3 } from '@babylonjs/core';
import { Level } from '../Level';
import { EnemyDistributionConfig } from '../EnemyGenerator';
import { ENEMY_ANIMATIONS, EnemyBehavior } from './EnemyBehavior';

/**
 * Attack behavior - idle normally, melee attack when player is very close
 */

export class EnemyAttackBehavior extends EnemyBehavior {
  private attackRange: number;
  private detectionRange: number;
  private isAttacking: boolean = false;
  private attackProgress: number = 0;
  private attackDuration: number = 0.5;
  private attackCooldown: number = 1.2;
  private cooldownTimer: number = 0;
  private attackTarget: Vector3 = Vector3.Zero();
  private attackStartPosition: Vector3 = Vector3.Zero(); // Position when attack started
  private useRightAttack: boolean = true; // Alternate attacks
  private playerWasInRange: boolean = false; // Track if player was in range before
  private spawnProtectionTime: number = 1.5; // Grace period when player enters range

  constructor(startPosition: Vector3, config: EnemyDistributionConfig) {
    super('enemyAttack', startPosition, config);
    this.attackRange = this.config.attackRange;
    this.detectionRange = this.config.detectionRange;
  }

  initLevel(level: Level): EnemyAttackBehavior {
    super.initLevel(level);
    this.level.game.sound.spatial('enemy_alert', this.mesh, 0, 30);
    this.level.game.sound.spatial('enemy_attack_swing', this.mesh, 0, 20);
    return this;
  }

  behaviorUpdate(deltaTime: number): void {
    this.cooldownTimer -= deltaTime;

    const playerDistance = this.getPlayerDistance();
    const playerPos = this.getPlayerPosition();
    const playerInRange = playerDistance <= this.detectionRange;

    // Spawn protection: when player first enters detection range, give them time to react
    if (playerInRange && !this.playerWasInRange) {
      this.cooldownTimer = Math.max(this.cooldownTimer, this.spawnProtectionTime);
      this.level?.game.sound.play('enemy_alert', this.mesh);
    }
    this.playerWasInRange = playerInRange;

    if (this.isAttacking) {
      // Perform attack animation
      this.attackProgress += deltaTime / this.attackDuration;

      if (this.attackProgress >= 1) {
        // Attack complete
        this.isAttacking = false;
        this.attackProgress = 0;
        this.cooldownTimer = this.attackCooldown;
        this.useRightAttack = !this.useRightAttack; // Alternate
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
      } else {
        // Lunge toward attack target - use sine wave for smooth in-out motion
        // Factor 0.9 ensures enemy reaches close to player for hit detection
        const lungePos = Vector3.Lerp(this.attackStartPosition, this.attackTarget, Math.sin(this.attackProgress * Math.PI) * 0.9);
        this.mesh.position.copyFrom(lungePos);
        this.snapToGround(deltaTime);
        this.lookAt(this.attackTarget);
      }
    } else if (playerDistance <= this.attackRange && this.cooldownTimer <= 0) {
      // Start attack - save current position as lunge start
      this.isAttacking = true;
      this.attackProgress = 0;
      this.attackStartPosition = this.mesh.position.clone();
      this.attackTarget = playerPos.clone();
      this.level?.game.sound.play('enemy_attack_swing', this.mesh);
      // Alternate between different attack animations
      const attackAnim = this.useRightAttack ? ENEMY_ANIMATIONS.ATTACK_MELEE_RIGHT : ENEMY_ANIMATIONS.ATTACK_MELEE_LEFT;

      // Try melee first, fallback to kick
      if (this.animations.has(attackAnim)) {
        this.playAnimation(attackAnim, false, 1.5);
      } else if (this.animations.has(ENEMY_ANIMATIONS.ATTACK_KICK_RIGHT)) {
        this.playAnimation(ENEMY_ANIMATIONS.ATTACK_KICK_RIGHT, false, 1.5);
      }
    } else if (playerDistance <= this.detectionRange) {
      // Player in detection range - approach slowly
      this.playAnimation(ENEMY_ANIMATIONS.WALK, true, 0.7);
      this.lookAt(playerPos);
      this.moveToward(playerPos, deltaTime * 0.4);
    } else {
      // Idle or return to start position
      const distToStart = Vector3.Distance(this.mesh.position, this.startPosition);
      if (distToStart > 0.5) {
        this.playAnimation(ENEMY_ANIMATIONS.WALK, true);
        this.moveToward(this.startPosition, deltaTime);
      } else {
        this.playAnimation(ENEMY_ANIMATIONS.IDLE, true);
        this.snapToGround(deltaTime);
      }
    }
  }

  clone(): EnemyAttackBehavior {
    return new EnemyAttackBehavior(this.startPosition, this.config);
  }
}
